import { useMemo } from 'react';
// locales
import { useLocales } from 'src/locales';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const iconOrganization = () => (
  <SvgColor src={`/assets/icons/components/ic_extra_organization_chart.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  settings: <Iconify icon="solar:settings-bold-duotone" />,
  building: <Iconify icon="solar:buildings-bold-duotone" />,
  organization: iconOrganization()
};



export const NAV_ITEMS_BY_ROLE = {
  resident: [
    {
      title: 'menu.Resident.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.Resident.users',
      path: '/users',
      icon: ICONS.user,
    },
  ],

  manager: [
    {
      title: 'menu.Manager.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.Manager.users',
      path: '/users',
      icon: ICONS.user,
    },
    {
      title: 'menu.Manager.building',
      path: '/building',
      icon: ICONS.building,
    },
    {
      title: 'menu.Manager.organizations',
      path: '/organization',
      icon: ICONS.organization,
    },
    {
      title: 'menu.Manager.invoice',
      path: '/invoice',
      icon: ICONS.invoice,
    },
    {
      title: 'menu.Manager.bankStatement',
      path: '/bank-statement',
      icon: ICONS.banking,
    },
    {
      title: 'menu.Manager.tickets',
      path: '/tickets',
      icon: ICONS.job,
    },
  ],

  organization_admin: [
    {
      title: 'menu.OrganizationAdmin.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.OrganizationAdmin.users',
      path: '/users',
      icon: ICONS.user,
    },
    {
      title: 'menu.Manager.building',
      path: '/building',
      icon: ICONS.building,
    },
    {
      title: 'menu.Manager.organizations',
      path: '/organization',
      icon: ICONS.organization,
    },
    {
      title: 'menu.OrganizationAdmin.invoice',
      path: '/invoice',
      icon: ICONS.invoice,
    },
    {
      title: 'menu.OrganizationAdmin.bankStatement',
      path: '/bank-statement',
      icon: ICONS.banking,
    },
    {
      title: 'menu.OrganizationAdmin.reports',
      path: '/reports',
      icon: ICONS.analytics,
    },
  ],

  super_admin: [
    {
      title: 'menu.SuperAdmin.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.SuperAdmin.users',
      path: '/users',
      icon: ICONS.user,
    },
    {
      title: 'menu.Manager.building',
      path: '/building',
      icon: ICONS.building,
    },
    {
      title: 'menu.Manager.organizations',
      path: '/organization',
      icon: ICONS.organization,
    },
    {
      title: 'menu.SuperAdmin.invoice',
      path: '/invoice',
      icon: ICONS.invoice,
    },
    {
      title: 'menu.SuperAdmin.bankStatement',
      path: '/bank-statement',
      icon: ICONS.banking,
    },
    {
      title: 'menu.SuperAdmin.reports',
      path: '/reports',
      icon: ICONS.analytics,
    },
  ],

  accountant: [
    {
      title: 'menu.Accountant.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.Accountant.users',
      path: '/users',
      icon: ICONS.user,
    },
    {
      title: 'menu.Manager.building',
      path: '/building',
      icon: ICONS.building,
    },
    {
      title: 'menu.Manager.organizations',
      path: '/organization',
      icon: ICONS.organization,
    },
    {
      title: 'menu.Accountant.invoice',
      path: '/invoice',
      icon: ICONS.invoice,
    },
    {
      title: 'menu.Accountant.bankStatement',
      path: '/bank-statement',
      icon: ICONS.banking,
    },
    {
      title: 'menu.Accountant.reports',
      path: '/reports',
      icon: ICONS.analytics,
    },
  ],

  worker: [
    {
      title: 'menu.Worker.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.Worker.users',
      path: '/users',
      icon: ICONS.user,
    },
    {
      title: 'menu.Manager.building',
      path: '/building',
      icon: ICONS.building,
    },
    {
      title: 'menu.Manager.organizations',
      path: '/organization',
      icon: ICONS.organization,
    },
    {
      title: 'menu.Worker.reports',
      path: '/reports',
      icon: ICONS.analytics,
    },
  ],

  technician: [
    {
      title: 'menu.Technician.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.Technician.users',
      path: '/users',
      icon: ICONS.user,
    },
    {
      title: 'menu.Manager.organizations',
      path: '/organization',
      icon: ICONS.organization,
    },
    {
      title: 'menu.Technician.reports',
      path: '/reports',
      icon: ICONS.analytics,
    },
  ],

  cleaner: [
    {
      title: 'menu.Cleaner.dashboard',
      path: '/dashboard',
      icon: ICONS.dashboard,
    },
    {
      title: 'menu.Cleaner.users',
      path: '/users',
      icon: ICONS.user,
    },
    {
      title: 'menu.Manager.organizations',
      path: '/organization',
      icon: ICONS.organization,
    },
    {
      title: 'menu.Cleaner.reports',
      path: '/reports',
      icon: ICONS.analytics,
    },
  ],
  
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();
  const userRoles = user?.roles?.length ? user.roles : ['resident'];

  const data = useMemo(() => {
    const items = userRoles.flatMap((role) => NAV_ITEMS_BY_ROLE[role] || []);

    const uniqueItems = items.filter(
      (item, index, self) => index === self.findIndex((i) => i.path === item.path)
    );

    return [
      {
        subheader: t('menu.operativa'),
        items: uniqueItems.map((item) => ({
          ...item,
          title: t(item.title),
        })),
      },
    ];
  }, [t, userRoles]);

  return data;
}
